import React, { Component, useContext } from "react";
import { withRouter } from "../../utils/router-compat";
import { withAuthentication } from "../Session";
import * as ROUTES from "../../constants/routes";
import Button from "../UI/Button";
import api from "../../utils/api";
import { setAuthToken } from "../../utils/api";
import { processError } from "../../utils/utils";
import { Context } from "../Cache";
import config from "../../utils/config";
import PhoneNumberInput from "../UI/PhoneNumberInput";

const SignInPage = () => {
  const { state } = useContext(Context);

  return (
    <div className="flex flex-col p-16 bg-white items-center">
      {state.config ? (
        <>
          {state.config.appearance.logo ? (
            <img
              className="h-auto object-contain w-48 border-none"
              src={state.config.appearance.logo}
            />
          ) : null}
          <div className="text-gray-900 text-lg font-bold text-center mt-6 mb-8">
            {state.config.appearance.name}
          </div>
        </>
      ) : null}
      <SignInForm />
    </div>
  );
};

class SignInFormBase extends Component {
  normalizePhone = (phone) => {
    if (!phone) return "";
    const trimmed = phone.trim();
    const digits = trimmed.replace(/\D/g, "");
    return trimmed.startsWith("+") ? `+${digits}` : `+${digits}`;
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      phoneNumber: "",
      verificationCode: "",
      error: null,
      showEnterCode: false,
      confirmationResult: null,
    };
  }

  componentDidMount() {
    this.redirectAuthenticatedUser(this.props.user);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.user !== this.props.user) {
      this.redirectAuthenticatedUser(this.props.user);
    }
  }

  redirectAuthenticatedUser = (user) => {
    if (!user) {
      return;
    }
    if (!user.whitelisted) {
      this.props.history.push(ROUTES.NOT_WHITELISTED);
      return;
    }
    const destination = user.admin
      ? ROUTES.ADMIN_TEAM
      : ROUTES.INCIDENTS_LIST;
    this.props.history.push(destination);
  };

  onEnterVerificationCode = async () => {
    const normalizedCode = this.state.verificationCode
      .replace(/\D/g, "")
      .slice(0, 6);
    if (normalizedCode.length !== 6) {
      this.setState({
        error: "Please enter the 6-digit verification code.",
      });
      return;
    }

    this.setState({
      loading: true,
      error: null,
      confirmationResult: null,
    });

    try {
      const normalizedPhone = this.normalizePhone(this.state.phoneNumber);
      const result = await api.post("/auth/phone/verifyToken", {
        verificationCode: normalizedCode,
        phoneNumber: normalizedPhone,
      });
      const token = result.data && result.data.token;
      if (!token) {
        this.setState({
          error: "Verification succeeded but no token was returned.",
          loading: false,
          showEnterCode: true,
        });
        return;
      }
      try {
        await this.props.firebase.doSignInWithToken(token);
      } catch (authError) {
        this.setState({
          error: authError?.message || "Unable to sign in with token.",
          loading: false,
          showEnterCode: true,
        });
        return;
      }
      try {
        const idToken = await this.props.firebase.getCurrentUserToken(true);
        setAuthToken(idToken);
      } catch (tokenError) {
        this.setState({
          error: tokenError?.message || "Unable to fetch session token.",
          loading: false,
          showEnterCode: true,
        });
        return;
      }
      this.setState({ loading: true, showEnterCode: true });
    } catch (error) {
      this.setState({
        error: processError(error, "Unable to verify code."),
        loading: false,
        showEnterCode: true,
      });
    }
  };

  onEnterPhoneNumber = async () => {
    this.setState({
      loading: true,
      error: null,
      confirmationResult: null,
    });

    let { phoneNumber } = this.state;
    const normalizedPhone = this.normalizePhone(phoneNumber);
    if (!normalizedPhone || normalizedPhone.replace(/\D/g, "").length < 7) {
      this.setState({
        error: "Please enter a valid phone number.",
        loading: false,
      });
      return;
    }

    try {
      await api.post("/auth/phone/sendToken", {
        phoneNumber: normalizedPhone,
      });
      this.setState({
        loading: false,
        showEnterCode: true,
      });
    } catch (error) {
      this.setState({ error: processError(error, "Unable to send verification code."), loading: false });
    }
  };

  onChange = (event) => {
    this.setState({ [event.target.name]: event.target.value });
  };

  render() {
    const { phoneNumber, verificationCode, error } = this.state;
    const errorText = error && (error.message || error);
    return (
      <>
        {this.state.showEnterCode ? (
          <div className="flex flex-col w-full max-w-md">
            <div className="text-gray-900 text-sm mb-4">
              Enter the SMS verification code. If you do not receive one, please
              wait 30 seconds and then refresh the page to request a new code.
            </div>
            <label className="block mb-4">
              <div className="text-gray-700">Verification code:</div>
              <input
                name="verificationCode"
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 mt-1"
                type="tel"
                value={verificationCode}
                onChange={this.onChange}
              />
            </label>
            <Button
              onClick={() => {
                this.onEnterVerificationCode();
              }}
              loading={this.state.loading}
              title={"submit"}
            />
            <div className="h-6" />
          </div>
        ) : (
          <div className="flex flex-col w-full max-w-md">
            <PhoneNumberInput
              className="mb-4"
              defaultValue={"+254"}
              onPhoneNumberUpdated={(phoneNumber) => {
                this.setState({ phoneNumber });
              }}
            />
            <Button
              onClick={() => {
                this.onEnterPhoneNumber();
              }}
              loading={this.state.loading}
              title={"submit"}
            />
            <div className="h-6" />
          </div>
        )}

        {errorText && (
          <p className="mt-2 text-sm text-red-600">{errorText}</p>
        )}
      </>
    );
  }
}

const SignInForm = withRouter(withAuthentication(SignInFormBase));

export default SignInPage;
export { SignInForm };
